import { defineStore, storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { useApi } from '@/composables/useApi';
import { cloneDeep, merge } from 'lodash';
import { ref, reactive, watch } from 'vue';

import themePresets from '@bss-praha/gastro-kiosk-themes';
import { useKioskStore } from '@/stores/useKioskStore';

export const useThemeStore = defineStore('theme', () => {
	const route = useRoute();
	const kioskStore = useKioskStore();
	const { kioskId } = storeToRefs(kioskStore);
	const { request } = useApi();

	const theme = ref(null);
	const externalTheme = ref(null);
	const loadedFonts = new Set();
	let availableFonts = null;

	// Получить список доступных тем
	const availableThemes = Object.entries(themePresets).map(([key, obj]) => ({
		key,
		label: obj.label,
		value: obj.value,
	}));

	function isMergeableObject(val) {
		return typeof val === 'object' && val !== null && !Array.isArray(val);
	}

	// Универсальное объединение объектов
	function deepMerge(target, source) {
		for (const key in source) {
			if (isMergeableObject(source[key]) && isMergeableObject(target[key])) {
				target[key] = deepMerge({ ...target[key] }, source[key]);
			} else {
				target[key] = source[key];
			}
		}
		return target;
	}

	// Раскрытие унаследованных элементов внутри темы
	function resolveTheme(rawTheme) {
		console.log('resolve theme');
		const theme = cloneDeep(rawTheme);

		function getByPath(obj, path) {
			return path.split('.').reduce((acc, key) => acc?.[key], obj);
		}

		function resolveInheritables(obj) {
			for (const [key, val] of Object.entries(obj)) {
				if (val?.inherit) {
					const base = getByPath(theme, val.inherit) || {};
					obj[key] = {
						...cloneDeep(base),
						...val,
						settings: merge({}, base.settings || {}, val.settings || {})
					};
				}
			}
		}

		for (const page of Object.values(theme.pages)) {
			if (!page.elements || !page.appearance) continue;
			resolveInheritables(page.appearance);
			resolveInheritables(page.elements);
		}

		const result = reactive(theme);
		console.log(result);
		return result;
	}

	// Загрузка темы с API
	async function fetchThemeFromApi() {
		console.log('fetch theme from api');
		try {
			const raw = await request(`/api/${kioskId.value}/template`);
			return resolveTheme(raw);
		} catch (err) {
			console.warn('Ошибка при загрузке темы с API:', err);
			return null;
		}
	}

	// Тема по умолчанию или из query ?theme=
	function getFallbackTheme() {
		const themeFromQuery = typeof route.query.themeId === 'string'
			? themePresets[route.query.themeId]
			: null;

		console.log('getting fallback theme');
		const result = resolveTheme(themeFromQuery || themePresets.defaultTheme);
		console.log(result);
		return result;
	}

	function applyExternalTheme(event) {
		const BACKOFFICE = process.env.VUE_APP_BACKOFFICE;

		if (event.origin !== BACKOFFICE) return;

		const resolved = resolveTheme(event.data);
		externalTheme.value = resolved;

		console.log('applying external theme');
		console.log(resolved);

		if (theme.value) {
			console.log('theme already exists, starting deepmerge');
			deepMerge(theme.value, resolved);
		} else {
			console.log('theme does not exist, apply resolved theme');
			theme.value = resolved;
		}
	}

	// Главный метод инициализации темы
	async function initTheme() {
		await getFonts();

		console.log('init theme');
		theme.value = getFallbackTheme();
		console.log(theme.value);

		if (externalTheme.value) {
			console.log('has external theme');
			theme.value = deepMerge(theme.value, externalTheme.value);
			return;
		}

		if (kioskId.value) {
			const themeFromApi = await fetchThemeFromApi();
			if (themeFromApi) {
				console.log('has theme from api');
				theme.value = deepMerge(theme.value, themeFromApi);
			}
		}
	}

	function getFontWeightsFromList(family) {
		if (!availableFonts) return;

		const font = availableFonts.find(f => f.family === family);
		if (!font) return null;

		const weights = new Set();

		for (const variant of font.variants) {
			if (variant === 'regular' || variant === 'italic') {
				weights.add('400');
			} else if (/^\d+$/.test(variant)) {
				weights.add(variant);
			} else if (/^\d+italic$/.test(variant)) {
				weights.add(variant.replace('italic', ''));
			}
		}

		return Array.from(weights).sort().join(';');
	}

	async function getFonts() {
		try {
			const response = await fetch(
				'https://www.googleapis.com/webfonts/v1/webfonts?key=AIzaSyAjB9wMXCi4hT4RncK6D56WNCsitaO9660'
			);
			const data = await response.json();

			availableFonts = data.items.map((font) => ({
				family: font.family,
				variants: font.variants
			}));
		} catch (error) {
			console.error('Error loading Google Fonts:', error);
		}
	}

	function extractFonts(obj, fonts = new Set()) {
		if (typeof obj !== 'object' || obj === null) return fonts;
		for (const key in obj) {
			const value = obj[key];
			if (key === 'fontFamily' && typeof value === 'string') {
				fonts.add(value);
			} else if (typeof value === 'object') {
				extractFonts(value, fonts);
			}
		}
		return fonts;
	}

	function loadFont(font) {
		if (loadedFonts.has(font)) return;

		const link = document.createElement('link');
		const weights = getFontWeightsFromList(font);

		link.rel = 'stylesheet';
		link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(font)}:wght@${weights}&display=swap`
		document.head.appendChild(link);
		loadedFonts.add(font);
	}

	// Следим за изменениями темы и шрифтов
	watch(theme, (value) => {
		if (!value) return;
		const fonts = extractFonts(value);
		fonts.forEach(loadFont);
	}, { deep: true, immediate: true });

	return {
		theme,
		availableThemes,
		initTheme,
		applyExternalTheme,
	};
});
